import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, CaretDown, Plus, Trash, PencilSimple, Question, BookOpen } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { Md } from '@/components/Md';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HelpPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isAdmin = user?.role === 'admin';
  const [sections, setSections] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [tab, setTab] = useState('manual');
  // Admin forms
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [sForm, setSForm] = useState({ category: 'Geral', title: '', content: '', steps: '', roles: '' });
  const [fForm, setFForm] = useState({ category: 'Geral', question: '', answer: '', roles: '' });

  const fetch = useCallback(async () => {
    try {
      const [sRes, fRes] = await Promise.all([
        axios.get(`${API}/api/help/sections`),
        axios.get(`${API}/api/help/faqs`),
      ]);
      setSections(sRes.data); setFaqs(fRes.data);
    } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const CATEGORIES = [...new Set([...sections.map(s => s.category), ...faqs.map(f => f.category)])].sort();

  // Search filter
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const q = norm(search);
  const filteredSections = q ? sections.filter(s => norm(s.title).includes(q) || norm(s.content).includes(q) || (s.steps || []).some(st => norm(st).includes(q))) : sections;
  const filteredFaqs = q ? faqs.filter(f => norm(f.question).includes(q) || norm(f.answer).includes(q)) : faqs;

  // Group by category
  const groupBy = (items) => {
    const g = {};
    items.forEach(i => { const c = i.category || 'Geral'; if (!g[c]) g[c] = []; g[c].push(i); });
    return g;
  };

  // CRUD
  const saveSection = async () => {
    const payload = { ...sForm, steps: sForm.steps ? sForm.steps.split('\n').filter(Boolean) : [], roles: sForm.roles ? sForm.roles.split(',').map(r => r.trim()).filter(Boolean) : [] };
    try {
      if (editId) await axios.put(`${API}/api/help/sections/${editId}`, payload);
      else await axios.post(`${API}/api/help/sections`, payload);
      toast.success('Secção guardada'); setShowSectionForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };
  const saveFaq = async () => {
    const payload = { ...fForm, roles: fForm.roles ? fForm.roles.split(',').map(r => r.trim()).filter(Boolean) : [] };
    try {
      if (editId) await axios.put(`${API}/api/help/faqs/${editId}`, payload);
      else await axios.post(`${API}/api/help/faqs`, payload);
      toast.success('FAQ guardada'); setShowFaqForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };
  const delSection = async (id) => {
    const s = sections.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a secção do manual "${s?.title || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/help/sections/${id}`); fetch();
  };
  const delFaq = async (id) => {
    const f = faqs.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a FAQ "${f?.question || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/help/faqs/${id}`); fetch();
  };

  return (
    <div className="fade-in" data-testid="help-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Ajuda</h2>
          <p className="text-xs text-brand-muted mt-0.5">Manual de utilização e perguntas frequentes</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar... Ex: como registo uma 1.ª consulta" className="pl-10 rounded-xl border-brand-border" data-testid="help-search" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setTab('manual')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'manual' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Manual</button>
        <button onClick={() => setTab('faq')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'faq' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>FAQ</button>
        {isAdmin && <button onClick={() => setTab('gerir')} className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${tab === 'gerir' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Gerir</button>}
      </div>

      {/* Manual */}
      {tab === 'manual' && (
        <div>
          {filteredSections.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
              <p className="text-brand-muted text-sm">{search ? 'Nenhum resultado para a pesquisa.' : 'Sem secções de ajuda.'}</p>
            </div>
          ) : (
            Object.entries(groupBy(filteredSections)).map(([cat, items]) => (
              <div key={cat} className="mb-5">
                <h3 className="text-xs font-semibold text-brand-green uppercase tracking-wider mb-2">{cat}</h3>
                <div className="space-y-2">
                  {items.map(s => (
                    <div key={s.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`help-section-${s.id}`}>
                      <h4 className="text-sm font-medium text-brand-text mb-1">{s.title}</h4>
                      <Md as="p" className="text-xs text-brand-muted leading-relaxed">{s.content}</Md>
                      {s.steps && s.steps.length > 0 && (
                        <ol className="mt-2 space-y-1 pl-4">
                          {s.steps.map((step, i) => (
                            <li key={i} className="text-xs text-brand-text list-decimal">{step}</li>
                          ))}
                        </ol>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* FAQ */}
      {tab === 'faq' && (
        <div>
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <Question size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
              <p className="text-brand-muted text-sm">{search ? 'Nenhum resultado para a pesquisa.' : 'Sem perguntas frequentes.'}</p>
            </div>
          ) : (
            Object.entries(groupBy(filteredFaqs)).map(([cat, items]) => (
              <div key={cat} className="mb-5">
                <h3 className="text-xs font-semibold text-brand-green uppercase tracking-wider mb-2">{cat}</h3>
                <div className="space-y-1.5">
                  {items.map(f => (
                    <div key={f.id} className="bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid={`faq-${f.id}`}>
                      <button onClick={() => setExpandedFaq(expandedFaq === f.id ? null : f.id)} className="w-full text-left p-4 flex items-center justify-between hover:bg-brand-bg/30">
                        <span className="text-sm font-medium text-brand-text">{f.question}</span>
                        <CaretDown size={14} className={`text-brand-placeholder transition-transform ${expandedFaq === f.id ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedFaq === f.id && (
                        <div className="px-4 pb-4 border-t border-brand-border/30">
                          <Md as="p" className="text-sm text-brand-muted leading-relaxed pt-3">{f.answer}</Md>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Admin: Manage */}
      {tab === 'gerir' && isAdmin && (
        <div>
          <div className="flex gap-2 mb-4">
            <button onClick={() => { setEditId(null); setSForm({ category: 'Geral', title: '', content: '', steps: '', roles: '' }); setShowSectionForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1"><Plus size={14} /> Nova secção</button>
            <button onClick={() => { setEditId(null); setFForm({ category: 'Geral', question: '', answer: '', roles: '' }); setShowFaqForm(true); }} className="border border-brand-border text-brand-green rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1"><Plus size={14} /> Nova FAQ</button>
          </div>
          <h3 className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-2">Secções do manual ({sections.length})</h3>
          <div className="space-y-1.5 mb-5">
            {sections.map(s => (
              <div key={s.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between">
                <div><p className="text-xs font-medium text-brand-text">{s.title}</p><p className="text-[9px] text-brand-muted">{s.category}{s.roles?.length ? ` · ${s.roles.join(', ')}` : ' · todos'}</p></div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditId(s.id); setSForm({ category: s.category, title: s.title, content: s.content, steps: (s.steps || []).join('\n'), roles: (s.roles || []).join(', ') }); setShowSectionForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={13} /></button>
                  <button onClick={() => delSection(s.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                </div>
              </div>
            ))}
          </div>
          <h3 className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-2">FAQs ({faqs.length})</h3>
          <div className="space-y-1.5">
            {faqs.map(f => (
              <div key={f.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between">
                <div><p className="text-xs font-medium text-brand-text">{f.question}</p><p className="text-[9px] text-brand-muted">{f.category}</p></div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditId(f.id); setFForm({ category: f.category, question: f.question, answer: f.answer, roles: (f.roles || []).join(', ') }); setShowFaqForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={13} /></button>
                  <button onClick={() => delFaq(f.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section form */}
      <Dialog open={showSectionForm} onOpenChange={setShowSectionForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar secção' : 'Nova secção'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Categoria</Label><Input value={sForm.category} onChange={e => setSForm({ ...sForm, category: e.target.value })} placeholder="Ex: Consultas" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={sForm.title} onChange={e => setSForm({ ...sForm, title: e.target.value })} placeholder="Ex: Registar uma consulta" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Conteúdo</Label><Textarea value={sForm.content} onChange={e => setSForm({ ...sForm, content: e.target.value })} className="rounded-xl border-brand-border" rows={3} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Passos (um por linha)</Label><Textarea value={sForm.steps} onChange={e => setSForm({ ...sForm, steps: e.target.value })} placeholder="Passo 1&#10;Passo 2&#10;Passo 3" className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Perfis (vazio = todos)</Label><Input value={sForm.roles} onChange={e => setSForm({ ...sForm, roles: e.target.value })} placeholder="admin, nutricionista" className="rounded-xl border-brand-border text-xs" /></div>
            <button onClick={saveSection} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ form */}
      <Dialog open={showFaqForm} onOpenChange={setShowFaqForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar FAQ' : 'Nova FAQ'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Categoria</Label><Input value={fForm.category} onChange={e => setFForm({ ...fForm, category: e.target.value })} placeholder="Ex: Consultas" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Pergunta</Label><Input value={fForm.question} onChange={e => setFForm({ ...fForm, question: e.target.value })} placeholder="Ex: Como registo uma 1.ª consulta?" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Resposta</Label><Textarea value={fForm.answer} onChange={e => setFForm({ ...fForm, answer: e.target.value })} className="rounded-xl border-brand-border" rows={4} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Perfis (vazio = todos)</Label><Input value={fForm.roles} onChange={e => setFForm({ ...fForm, roles: e.target.value })} placeholder="admin, nutricionista" className="rounded-xl border-brand-border text-xs" /></div>
            <button onClick={saveFaq} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
